import React, { useEffect, useState } from 'react';
import axios from 'axios';

const isLocal = window.location.hostname === 'localhost';
const baseUrl = isLocal
  ? 'http://localhost:5000'
  : 'https://www.trendscout.club';

// 🔍 Fetch Binance Wallet (Spot + Futures + Prices + CRG baseline)
export const useBinanceWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        // 1️⃣ Fetch wallet data
        const res = await axios.get(`${baseUrl}/api/binance/wallet`);

        // 2️⃣ Fetch live Binance prices
        const priceRes = await axios.get('https://api.binance.com/api/v3/ticker/price');
        const prices = {};
        priceRes.data.forEach(p => { prices[p.symbol] = parseFloat(p.price); });

        // 3️⃣ Fetch fund-sharing bots to find CRG
        const botsRes = await axios.get(`${baseUrl}/api/fund-sharing/bots`);
        const crgBot = (botsRes.data?.bots || []).find(
          b => b.name && b.name.toLowerCase().includes('crg')
        );

        // 4️⃣ Merge everything into one wallet object
        setWallet({
          ...res.data,
          prices,
          crgTradingAmount: crgBot?.tradingAmount || 0, // 💰 baseline for CRG
        });
      } catch (err) {
        console.error('❌ Failed to fetch wallet or CRG baseline:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWallet();
  }, []);

  return { wallet, isLoading: loading };
};


// 🔍 Fetch Open Futures Positions
export const useBinancePositions = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPositions = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/api/binance/positions`);
      setPositions(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch positions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPositions(); }, [fetchPositions]);

  return { positions, isLoading: loading, refetch: fetchPositions };
};


// 🔍 Fetch Trade Performance Summary
export const useTradeSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/binance/wallet/summary`);
        setSummary(res.data);
      } catch (err) {
        console.error('❌ Failed to fetch trade summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  return { summary, isLoading: loading };
};

// 🔍 Fetch Realized PNL from Binance
export const useRealizedPnl = () => {
  const [realizedPnl, setRealizedPnl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRealizedPnl = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/binance/wallet/realized-pnl`);
        setRealizedPnl(res.data); // <--- return full object, not .totalPnl
      } catch (err) {
        console.error('❌ Failed to fetch realized PNL:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRealizedPnl();
  }, []);

  return { realizedPnl, isLoading: loading };
};


